import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Friendly message for upload failures, including proxy/network errors that carry no JSON detail.
export function uploadErrorMessage(e) {
  if (!e?.response) return "Network problem while uploading — check your connection and try again.";
  const st = e.response.status;
  if (st === 413) return "File is too large to upload. Max 250 MB for audio.";
  if (st === 401) return "Your session expired — please log in again and retry.";
  const d = e.response.data?.detail;
  if (d) return formatApiError(d);
  if (st >= 500) return "Server is busy — please retry the upload in a moment.";
  return "Upload failed — please try again.";
}

// Small cached WEBP for decorative images (cover ring/strip) — avoids multi-MB originals.
export const thumbUrl = (src, w = 480) => `${API}/media/thumb?w=${w}&src=${encodeURIComponent(src)}`;

export const coverThumb = (path, w = 320) => thumbUrl(`/api/files/public?path=${encodeURIComponent(path)}`, w);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail === "object") {
    // Structured errors like { error, toolost_status, toolost_body, connect }
    // — surface the most useful text instead of "[object Object]".
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.error === "string") {
      // Append the DSP body if it's a readable string / object with a hint.
      const body = detail.toolost_body ?? detail.tonegrid_body ?? detail.body;
      if (typeof body === "string" && body.trim()) {
        return `${detail.error} — ${body.slice(0, 240)}`;
      }
      if (body && typeof body === "object") {
        const readable = body.error || body.message || (Array.isArray(body.errors) && body.errors.join("; "));
        if (readable) return `${detail.error} — ${String(readable).slice(0, 240)}`;
      }
      return detail.error;
    }
    if (typeof detail.detail === "string") return detail.detail;
    if (typeof detail.message === "string") return detail.message;
    try { return JSON.stringify(detail).slice(0, 400); } catch { return "Unknown error"; }
  }
  return String(detail);
}

// Uploads that need conversion return {job_id, status:"processing"}; poll until the
// background job finishes so every caller still receives the final file payload.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
api.interceptors.response.use(async (res) => {
  const u = res.config?.url || "";
  if ((u === "/files/upload" || (u.startsWith("/files/upload/chunked/") && u.endsWith("/complete"))) && res.data?.job_id && res.data?.status === "processing") {
    const started = Date.now();
    while (Date.now() - started < 10 * 60 * 1000) {
      await sleep(1500);
      let data;
      try {
        ({ data } = await api.get(`/files/upload-jobs/${res.data.job_id}`));
      } catch (e) {
        if ([401, 403, 404].includes(e.response?.status)) throw e;
        continue; // transient network/5xx while polling — keep waiting
      }
      if (data.status === "done") return { ...res, data: data.result };
      if (data.status === "failed") {
        const err = new Error(data.error || "Upload failed");
        err.response = { status: 400, data: { detail: data.error || "Upload failed" } };
        throw err;
      }
    }
    const err = new Error("Upload timed out");
    err.response = { status: 504, data: { detail: "Processing took too long — please retry." } };
    throw err;
  }
  return res;
});

// Upload any file; big audio/cover files go up in 5 MB pieces so lossless WAV/FLAC
// masters never hit proxy size limits. Pieces retry automatically on network blips.
export async function uploadFile(file, kind, onProgress) {
  const report = (p) => onProgress && onProgress(Math.min(100, Math.round(p)));
  if (!["audio", "cover"].includes(kind) || file.size <= 8 * 1024 * 1024) {
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    const { data } = await api.post("/files/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (ev) => ev.total && report((ev.loaded / ev.total) * 100),
    });
    return data;
  }
  const { data: init } = await api.post("/files/upload/chunked/init", { filename: file.name, kind, size: file.size });
  const step = init.chunk_size;
  for (let offset = 0; offset < file.size; offset += step) {
    const piece = file.slice(offset, offset + step);
    for (let attempt = 0; ; attempt++) {
      try {
        await api.post(`/files/upload/chunked/${init.upload_id}?index=${offset / step}`, piece, {
          headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (ev) => report(((offset + (ev.loaded || 0)) / file.size) * 98),
        });
        break;
      } catch (e) {
        if (attempt >= 4 || (e.response && e.response.status < 500 && e.response.status !== 429)) throw e;
        await sleep(1500 * (attempt + 1));
      }
    }
  }
  const { data } = await api.post(`/files/upload/chunked/${init.upload_id}/complete`);
  report(100);
  return data;
}
